import { ChangeHistoryEmployeeData } from 'nomst/Stores/ChangeHistory/changeHistoryEmployeeData';
import { IChangeHistoryItem } from 'typings/MobxComponents/changeHistoryItem';
import { ChangeHistoryItem } from 'typings/enums';
import { ChangeHistoryEmployeeCycleState } from 'nomst/Stores/ChangeHistory/changeHistoryEmployeeCycleState';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import AmountConvert from 'common/amountConvert';
import {
    AmountPayType
} from 'nomst/Stores/Configuration/Constants/amountPayType';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { User } from 'nomst/Stores/User/user';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import { IAmount } from 'typings/amount';
import { Amount } from 'nomst/Stores/Decision/amount';
import {
    PercentageCalculationRule
} from 'nomst/Stores/Configuration/Constants/percentageCalculationRule';
import SafeMath from 'common/safeMath';
import { AmountCode } from 'typings/enums';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';

export class ChangeHistoryEmployeeCycle implements IChangeHistoryItem {
    employee: ChangeHistoryEmployeeData;
    modifyingUser: User;
    code: string = ChangeHistoryItem.ChangeHistoryEmployeeCycle;
    stateBeforeChange: ChangeHistoryEmployeeCycleState;
    stateAfterChange: ChangeHistoryEmployeeCycleState;

    private readonly _configuration: Configuration;
    private readonly _currencyConversion: CurrencyConversion;

    constructor(
        changeHistoryItemDto: IChangeHistoryItem,
        configuration: Configuration,
        currencyConversion: CurrencyConversion) {
        this._configuration = configuration;
        this._currencyConversion = Coalesce.singleValue(currencyConversion, () => new CurrencyConversion(null));
        if (changeHistoryItemDto.employee) {
            this.employee = new ChangeHistoryEmployeeData(changeHistoryItemDto.employee);
        }

        if (changeHistoryItemDto.stateBeforeChange) {
            this.stateBeforeChange = new ChangeHistoryEmployeeCycleState(
                changeHistoryItemDto.stateBeforeChange.promotionJobTitle,
                this.createAmounts(changeHistoryItemDto.stateBeforeChange.amounts),
                changeHistoryItemDto.stateBeforeChange.countryBudgets);
        }
        if (changeHistoryItemDto.stateAfterChange) {
            this.stateAfterChange = new ChangeHistoryEmployeeCycleState(
                changeHistoryItemDto.stateAfterChange.promotionJobTitle,
                this.createAmounts(changeHistoryItemDto.stateAfterChange.amounts),
                changeHistoryItemDto.stateAfterChange.countryBudgets);
        }
        this.modifyingUser = changeHistoryItemDto.modifyingUser;
    }

    get currencyConversion(): CurrencyConversion {
        return this._currencyConversion;
    }

    employeeCurrentPay(): number {
        return this.employee.isHourly ? this.employee.currentPay : this.employee.currentPayWithFte();
    }

    annualCurrentPay(): number {
        return AmountConvert.convertToAnnual(
            this.employeeCurrentPay(),
            this.employee.annualStandardWorkHours,
            this.employee.isHourly,
            this.employee.fullTimeEquivalent);
    }

    getAmountPercentage(value: number, annualOnly: boolean = false): number {
        const currentPay = annualOnly && this.employee.isHourly
            ? this.annualCurrentPay()
            : this.employeeCurrentPay();

        return SafeMath.divide(value, currentPay);
    }

    calculateNewPayAmounts(employeeCycleState: ChangeHistoryEmployeeCycleState): number {
        return employeeCycleState.amounts
            .filter(amt => amt.payType === AmountPayType.BasePay)
            .reduce<number>((accumulator, amt) => {
                return accumulator + amt.value;
            }, 0);
    }

    newPay(employeeCycleState: ChangeHistoryEmployeeCycleState): number {
        return this.employeeCurrentPay() + this.calculateNewPayAmounts(employeeCycleState);
    }

    calculateTccAmounts(employeeCycleState: ChangeHistoryEmployeeCycleState): number {
        return employeeCycleState.amounts
            .filter(amount => this._configuration.teamPageViews.doesAmountUseTccField(amount.amountCode))
            .reduce<number>((accumulator, amount) => {
                const value = amount.payType === AmountPayType.BasePay
                    ? AmountConvert.convertToAnnual(
                        amount.value,
                        this.employee.annualStandardWorkHours,
                        this.employee.isHourly,
                        this.employee.fullTimeEquivalent)
                    : amount.value;
                return accumulator + value;
            }, 0);
    }

    tcc(employeeCycleState: ChangeHistoryEmployeeCycleState): number {
        return this.annualCurrentPay() + this.calculateTccAmounts(employeeCycleState);
    }

    private createAmounts(amounts: IAmount[]): Amount[] {
        return amounts.map(amount => {
            return new Amount(
                amount as Amount,
                this.employee.employeeSourceId,
                this.employee.currentPay,
                null,
                this.getPercentCalculationRule(amount.amountCode),
                this.getPayType(amount.amountCode),
                null,
                null,
                this._currencyConversion,
                null);
        });
    }

    private getPayType(amountCode: AmountCode): Nullable<AmountPayType> {
        const cycleBudgetField = this.getCycleBudgetFieldByCode(amountCode);
        if (cycleBudgetField) {
            return cycleBudgetField.payType;
        }
        return null;
    }

    private getPercentCalculationRule(amountCode: AmountCode): Nullable<PercentageCalculationRule> {
        const cycleBudgetField = this.getCycleBudgetFieldByCode(amountCode);
        if (cycleBudgetField) {
            return cycleBudgetField.percentageCalculationRule;
        }

        return null;
    }

    private getCycleBudgetFieldByCode(amountCode: AmountCode): CycleBudgetField|undefined {
        return this._configuration.cycleBudgetFields?.find(
            (cycleBudgetField: CycleBudgetField) => cycleBudgetField.amountCode === amountCode);
    }
}